import { type ChangeEvent, type FormEvent, Fragment, memo, useRef, useState } from "react";
import { Button, ButtonTypes, ConfirmModal, type ModalButtonProps, Input, Modal } from "@clintonelec/react-storybook";
import "Components/ConfigLayout/System/ManagementSettings/FirmwareUpgradeModal/FirmwareUpgradeModal.less";
import { useAppSelector } from "Data/Redux/Store";
import { selectSystemInformation } from "Data/Redux/Slices/Settings/System/Information";
import ProgressBarModal from "Components/ConfigLayout/System/ManagementSettings/ProgressBarModal";

const confirmButton: ModalButtonProps = {
	text: "Confirm",
	type: ButtonTypes.TERTIARY
};

const cancelButton: ModalButtonProps = {
	text: "Cancel",
	type: ButtonTypes.DANGER
};

function SystemDataModal() {
	const systemInfo = useAppSelector(selectSystemInformation);
	const [ visible, setVisible ] = useState(false);
	const fileSelectRef = useRef<HTMLInputElement>(null);
	const [ selectedFile, setSelectedFile ] = useState<File | null>(null);
	const firmwareUpgradeProgressBarModalRef = useRef<HTMLButtonElement>(null);

	const handleVisibilityChange = (newVisibility: boolean) => {
		setSelectedFile(null);
		setVisible(newVisibility);
	};

	const handleSelectedFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = event.target?.files;

		if (!selectedFiles || selectedFiles?.length === 0) {
			return;
		}

		setSelectedFile(selectedFiles[0]);
	};

	const handleFirmwareUpload = (event: FormEvent<Element>) => {
		event.preventDefault();
		event.stopPropagation();

		setVisible(false);
		firmwareUpgradeProgressBarModalRef.current?.click();
	};

	const renderWarning = () => {
		if (!systemInfo.model.startsWith("CE-PV")) {
			return;
		}

		return (
			<div className="note-banner alert">
				<span>Note</span>
				<span>&nbsp;-&nbsp;</span>
				<span>
					If the camera is installed in a Connect PVM, the Connect PVM should be set to Maintenance Mode
					before updating the camera firmware to prevent possible firmware update interruption. For more
					information please visit our support page&nbsp;
					<a target="_blank" href="https://support.clintonelectronics.com/" rel="noreferrer noopener">here</a>
					.
				</span>
			</div>
		);
	};

	const modalContent = (
		<div className="firmware-upgrade-modal-content">
			{ renderWarning() }
			<p>Please select file for Firmware Upgrade</p>
			<div className="file-selector">
				<input
					accept=".bin"
					hidden
					onChange={ handleSelectedFileChange }
					ref={ fileSelectRef }
					type="file"
				/>
				<Input
					className="file-label"
					disabled
					value={ selectedFile?.name }
				/>
				<Button
					onClick={ () => fileSelectRef.current?.click() }
					htmlType="button"
					type={ ButtonTypes.TERTIARY }
				>
					Choose File
				</Button>
			</div>
			<ConfirmModal
				okButton={ { ...confirmButton, onClick: handleFirmwareUpload } }
				cancelButton={ cancelButton }
				title="Firmware Upgrade"
				modalContent="Video recording will be stopped automatically."
			>
				<Button type={ ButtonTypes.TERTIARY } disabled={ !selectedFile } htmlType="button">
					Upload
				</Button>
			</ConfirmModal>
		</div>
	);

	return (
		<Fragment>
			<Modal
				title="Firmware Upgrade"
				modalContent={ modalContent }
				onVisibilityChange={ handleVisibilityChange }
				visible={ visible }
				width={ 550 }
			>
				<Button
					htmlType="button"
					type={ ButtonTypes.TERTIARY }
				>
					Load
				</Button>
			</Modal>
			<ProgressBarModal
				inProgressMessage="Firmware Upgrade..."
				completeMessage="Firmware upgrade completed successfully."
				title="Firmware Upgrade"
				totalDuration={ 10 }
			>
				<button ref={ firmwareUpgradeProgressBarModalRef } hidden type="button" />
			</ProgressBarModal>
		</Fragment>
	);
}

export default memo(SystemDataModal);
